import type { OneSchemaJWTResponse } from '@/types/api/one-schema'

import { toError } from '@moment/observability/logger'
import OneSchemaImporter, { type ImporterCustomization } from '@oneschema/react'
import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'

import { MomentEnv } from '@/app/moment-env'
import { useAppDispatch } from '@/app/store'
import Button, { ButtonVariant } from '@/components/atomic/button'
import Icon, { IconName } from '@/components/atomic/icon'
import { generateJWT } from '@/legacy-api/one-schema-slice'
import { createLogger } from '@/lib/logger'
import { setModelField } from '@/reducers/model-builder'
import { Environment } from '@/types/enums/environments'

const LOGGER = createLogger('components/pages/model-builder/ticker-bulk-upload')

// One schmea constants
const ONE_SCHEMA_CLIENT_ID = MomentEnv.OneSchemaClientId
const ONE_SCHEMA_TEMPLATE_KEY = 'model_builder_ticker_upload'
const MAX_ROWS_PER_UPLOAD = 8000
const CUSTOMIZATION_OVERRIDES =
  MomentEnv.Environment === Environment.STAGING
    ? {}
    : ({
        importMaxRowLimit: MAX_ROWS_PER_UPLOAD,
        importRowLimitHeader: `Maximum file size exceeded`,
        importRowLimitDescription: `The maximum number of rows per upload in this environment is ${MAX_ROWS_PER_UPLOAD}.`,
        importErrorUX: 'blockIfErrors',
      } as ImporterCustomization)

type TickerSpreadsheetRecord = {
  ticker: string
}

type ImportResult = {
  records: TickerSpreadsheetRecord[]
  error_records: {
    data: TickerSpreadsheetRecord[]
    error: Record<keyof TickerSpreadsheetRecord, unknown[]>
  }[]
}

interface TickerBulkUploadProps {
  field: 'includeTickers' | 'excludeTickers'
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function TickerBulkUpload({ field }: TickerBulkUploadProps) {
  const dispatch = useAppDispatch()

  // -- Local state --
  const [isImporterOpen, setIsImporterOpen] = useState(false)
  const [oneSchemaUserJwt, setOneSchemaUserJwt] = useState<string | null>(null)

  // -- Handlers --
  const onLaunch = useCallback(async () => {
    await dispatch(
      generateJWT.initiate({
        metadata: {}, // We may want to pass in validationData here in the future, but for now we don't need it
      }),
    )
      .unwrap()
      .then((res: OneSchemaJWTResponse) => {
        setOneSchemaUserJwt(res.token)
        setIsImporterOpen(true)
      })
      .catch((error) => {
        LOGGER.error('Failed to generate OneSchema JWT', {
          error: toError(error),
        })
      })
  }, [dispatch])

  const onImportSuccess = useCallback(
    async (data: ImportResult) => {
      const tickers = data.records.map((record) => record.ticker)

      dispatch(setModelField({ field, value: tickers }))
    },
    [dispatch, field],
  )

  return (
    <div>
      <Button
        icon={<Icon iconName={IconName.ArrowUpTrayIcon} size={'small'} />}
        iconSide="left"
        variant={ButtonVariant.Secondary}
        label="Upload"
        onClick={onLaunch}
      />
      {createPortal(
        <OneSchemaImporter
          isOpen={isImporterOpen}
          onRequestClose={() => setIsImporterOpen(false)}
          // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
          clientId={ONE_SCHEMA_CLIENT_ID!}
          userJwt={oneSchemaUserJwt ?? ''}
          templateKey={ONE_SCHEMA_TEMPLATE_KEY}
          importConfig={{ type: 'local' }}
          devMode={false}
          customizationOverrides={CUSTOMIZATION_OVERRIDES}
          className="oneschema-importer"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1000,
          }}
          onSuccess={onImportSuccess}
        />,
        document.body,
      )}
    </div>
  )
}
